import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import {
  getGetDealShortlistQueryKey,
  useDeleteLimitedPartnerFromShortlist,
} from "@vega-api/generated/coinvest-deal-shortlist/coinvest-deal-shortlist";
import { type DealShortlistItemResponse } from "@vega-api/generated/types/dealShortlistItemResponse";
import { useToast } from "@vega-core/components/Toast/useToast";
import { useBoolean } from "@vega-core/hooks/useBoolean";
import { m } from "@vega-core/paraglide/messages";
import { useCoInvestErrorToast } from "@vega-modules/hooks/useCoInvestErrorToast";

type UseDeleteProspectParams = {
  dealId: string;
  onRemoveSuccess?: (prospectId: string) => void;
  prospectsData: DealShortlistItemResponse[];
};

export const useDeleteProspect = ({
  dealId,
  onRemoveSuccess,
  prospectsData,
}: UseDeleteProspectParams) => {
  const queryClient = useQueryClient();
  const showToast = useToast();
  const showErrorToast = useCoInvestErrorToast();

  const {
    isOpen: showRemoveConfirmation,
    setTrue: openRemoveConfirmation,
    setFalse,
  } = useBoolean(false);

  const [selectedProspectForRemoval, setSelectedProspectForRemoval] = useState<
    string | null
  >(null);

  const closeRemoveConfirmation = () => {
    setFalse();
    setSelectedProspectForRemoval(null);
  };

  const deleteMutation = useDeleteLimitedPartnerFromShortlist({
    mutation: {
      onSuccess: () => {
        const removedId = selectedProspectForRemoval;
        closeRemoveConfirmation();

        if (removedId) {
          onRemoveSuccess?.(removedId);
        }
        showToast({
          title: m.status_success(),
          variant: "success",
        });
        queryClient.invalidateQueries({
          queryKey: getGetDealShortlistQueryKey(dealId),
        });
      },
      onError: (error) => {
        setSelectedProspectForRemoval(null);
        showErrorToast(error, {
          defaultMessage: m.coInvestments_error_removeProspects(),
        });
      },
    },
  });

  const handleRemoveIndividualProspect = (prospectId: string) => {
    const limitedPartnerId = prospectsData.find(
      (prospect) => prospect.id === prospectId,
    )?.limitedPartner.id;

    if (!dealId || !limitedPartnerId) return;

    deleteMutation.mutate({
      dealId,
      data: {
        limitedPartnerIds: [limitedPartnerId],
      },
    });
  };

  const handleShowIndividualRemoveConfirmation = (prospectId: string) => {
    setSelectedProspectForRemoval(prospectId);
    openRemoveConfirmation();
  };

  return {
    showRemoveConfirmation,
    selectedProspectForRemoval,
    handleRemoveIndividualProspect,
    handleShowIndividualRemoveConfirmation,
    closeRemoveConfirmation,
    isDeleting: deleteMutation.isPending,
  };
};
